<template>
  <div>
    <vx-card no-shadow class="mb-base">
      <!-- Col Header -->
      <div class="flex items-end" v-if="showHeader">
        <feather-icon icon="FolderIcon" class="mr-2" svgClasses="w-5 h-5" />
        <span class="leading-none font-medium">{{ $t('employees.employee_panel') }}</span>
      </div>
      <label class="text-md font-bold">{{ $t('employees.known_langs') }}</label>
      <v-select
        name="locale"
        v-model="locales"
        :placeholder="$t('forms.select_option')"
        multiple
        taggable
        clearable
        :options="[
          { label: $t('general.persian'), value: 'fa' },
          { label: $t('general.english'), value: 'en' },
          { label: $t('general.arabic'), value: 'ar' }
        ]"
        :reduce="option => option.value"
        :dir="$vs.rtl ? 'rtl' : 'ltr'"
        class="w-full mb-base"
      >
      </v-select>
      <vs-row class="vx-col w-full" vs-justify="flex-end">
        <vs-button class="mr-2 flex-end" @click="saveLocales" color="success">
          {{ $t('forms.save') }}
        </vs-button>
      </vs-row>
    </vx-card>
    <vx-card no-shadow>
      <div class="vx-row vs-con-loading__container" id="tab-loading">
        <div class="vx-col w-full">
          <!-- Col Content -->
          <div class="w-full mt-4">
            <vs-row class="mb-base" v-if="document.state === 'pending'">
              <vs-alert>{{ $t('documents.pending_msg') }}</vs-alert>
            </vs-row>
            <div class="vx-row" v-if="createDocument">
              <div class="vx-col w-full">
                <vx-card :title="$t('documents.financial_info')" class="mb-base" no-shadow card-border>
                  <div class="vx-row">
                    <div class="vx-col w-full md:w-1/2 md:pr-5 mt-3">
                      <vs-input
                        v-model="document.bankName"
                        class="w-full"
                        name="bankName"
                        v-validate="'required'"
                        :data-vv-as="$t('documents.bank_name')"
                        :label="$t('documents.bank_name')"
                      />
                      <span class="text-danger text-sm" v-show="errors.has('bankName')">{{
                        errors.first('bankName')
                      }}</span>
                    </div>
                    <div class="vx-col w-full md:w-1/2 md:pr-5 mt-3">
                      <vs-input
                        v-model="document.branchNo"
                        class="w-full"
                        name="branchNo"
                        v-validate="'required'"
                        :data-vv-as="$t('documents.branch_no')"
                        :label="$t('documents.branch_no')"
                      />
                      <span class="text-danger text-sm" v-show="errors.has('branchNo')">{{
                        errors.first('branchNo')
                      }}</span>
                    </div>
                    <div class="vx-col w-full md:pr-5 mt-3">
                      <vs-input
                        v-model="document.IBAN"
                        dir="ltr"
                        class="w-full"
                        name="IBAN"
                        v-validate="'required|regex:^IR[0-9]{24}$'"
                        :data-vv-as="$t('documents.iban')"
                        :label="$t('documents.iban')"
                      />
                      <span class="text-warning text-sm">{{ $t('documents.iban_tip') }}</span
                      ><br />
                      <span class="text-danger text-sm" v-show="errors.has('IBAN')">{{
                        errors.first('IBAN')
                      }}</span>
                    </div>
                    <div class="vx-col w-full md:pr-5 mt-3">
                      <vs-input
                        v-model="document.creditCard"
                        dir="ltr"
                        class="w-full"
                        name="creditCard"
                        v-validate="'required|regex:^[0-9]{16}$'"
                        :data-vv-as="$t('documents.credit_card')"
                        :label="$t('documents.credit_card')"
                      />
                      <span class="text-warning text-sm">{{ $t('documents.credit_card_tip') }}</span
                      ><br />
                      <span class="text-danger text-sm" v-show="errors.has('creditCard')">{{
                        errors.first('creditCard')
                      }}</span>
                    </div>
                  </div>
                </vx-card>
              </div>
              <div class="vx-col w-full">
                <vx-card :title="$t('documents.docs_and_grades')" class="mb-base" no-shadow card-border>
                  <div class="vx-row">
                    <div class="vx-col w-full md:w-1/2 md:pr-5 mt-3">
                      <vs-input
                        v-model="document.grade"
                        class="w-full"
                        name="grade"
                        v-validate="'required'"
                        :data-vv-as="$t('documents.grade')"
                        :label="$t('documents.grade')"
                      />
                      <span class="text-danger text-sm" v-show="errors.has('grade')">{{
                        errors.first('grade')
                      }}</span>
                    </div>
                    <div class="vx-col w-full md:w-1/2 md:pr-5 mt-3">
                      <vs-input
                        v-model="document.fieldOfStudy"
                        class="w-full"
                        name="fieldOfStudy"
                        v-validate="'required'"
                        :data-vv-as="$t('documents.field_of_study')"
                        :label="$t('documents.field_of_study')"
                      />
                      <span class="text-danger text-sm" v-show="errors.has('fieldOfStudy')">{{
                        errors.first('fieldOfStudy')
                      }}</span>
                    </div>
                    <div class="vx-col w-full md:w-1/2 md:pr-5 mt-3">
                      <label class="text-sm">{{ $t('documents.license_no') }}</label>
                      <v-select
                        :options="document.licenseNo"
                        :clearable="false"
                        taggable
                        multiple
                        :dir="$vs.rtl ? 'rtl' : 'ltr'"
                        v-model="document.licenseNo"
                        name="licenseNo"
                        v-validate="'required'"
                        :data-vv-as="$t('documents.license_no')"
                      />
                      <span class="text-warning text-sm">{{ $t('documents.licenseNo_tip') }}</span
                      ><br />
                      <span class="text-danger text-sm" v-show="errors.has('licenseNo')">{{
                        errors.first('licenseNo')
                      }}</span>
                    </div>
                    <div class="vx-col w-full md:pr-5 mt-3">
                      <label class="text-sm">{{ $t('documents.attachments') }}</label>
                      <upload-file
                        v-model="document.attachments"
                        multiple
                        ref="uploads"
                        :start-upload="startUpload"
                        @pending-upload="handlePendingUpload"
                      />
                    </div>
                    <div class="vx-col w-full md:pr-5 mt-3">
                      <label class="text-sm">{{ $t('documents.description') }}</label>
                      <vs-textarea
                        v-model="document.description"
                        class="w-full"
                        name="description"
                        :data-vv-as="$t('documents.description')"
                      />
                      <span class="text-danger text-sm" v-show="errors.has('description')">{{
                        errors.first('description')
                      }}</span>
                    </div>
                  </div>
                </vx-card>
              </div>
              <vs-row class="vx-col w-full" vs-justify="flex-end">
                <vs-button class="mr-2 flex-end" @click="saveDocuments" color="success">
                  {{ canSubmit ? $t('forms.submit') : $t('upload_files.upload_file') }}
                </vs-button>
              </vs-row>
            </div>
            <div class="vx-row" v-else>
              <vx-card no-shadow card-border class="mb-base" v-if="document.state === 'approved'">
                <vs-row>
                  <div class="vx-col mb-3 w-full md:w-1/3" v-if="document.expirationDate">
                    <vs-row class="font-semibold">{{ $t('documents.expiration_date') }}</vs-row>
                    <vs-row class="mt-3">{{ document.expirationDate | datetime }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full md:w-2/3" v-if="document.approvedSubjects.length">
                    <vs-row class="font-semibold">{{ $t('documents.approved_subjects') }}</vs-row>
                    <vs-row class="mt-3">
                      <span v-for="subject in document.approvedSubjects" :key="subject" class="pl-2">
                        &#9679; {{ $t(`places.subjects.${subject}`) }}
                      </span>
                    </vs-row>
                  </div>
                </vs-row>
              </vx-card>
              <vx-card
                no-shadow
                card-border
                class="mb-base"
                content-color="danger"
                v-if="document.state === 'rejected'"
              >
                <vs-row>
                  <div class="vx-col mb-3 w-full md:w-4/5">
                    <vs-row class="font-semibold">{{ $t('documents.rejected_because') }}</vs-row>
                    <vs-row class="mt-3">{{ document.rejectMessage }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full md:w-1/5">
                    <vs-button @click="resubmit">{{ $t('documents.resubmit') }}</vs-button>
                  </div>
                </vs-row>
              </vx-card>
              <div class="vx-col mb-3 w-full md:w-1/5">
                <h4>{{ $t('documents.financial_info') }}</h4>
              </div>
              <div class="vx-col mb-3 w-full md:w-4/5">
                <vs-row>
                  <div class="vx-col mb-3 w-full md:w-1/3">
                    <vs-row class="font-semibold">{{ $t('documents.bank_name') }}</vs-row>
                    <vs-row class="mt-3">{{ document.bankName }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full md:w-1/3">
                    <vs-row class="font-semibold">{{ $t('documents.branch_no') }}</vs-row>
                    <vs-row class="mt-3">{{ document.branchNo }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full">
                    <vs-row class="font-semibold">{{ $t('documents.iban') }}</vs-row>
                    <vs-row class="mt-3">{{ document.IBAN }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full">
                    <vs-row class="font-semibold">{{ $t('documents.credit_card') }}</vs-row>
                    <vs-row class="mt-3">{{ document.creditCard }}</vs-row>
                  </div>
                </vs-row>
              </div>
              <div class="vx-col mb-3 w-full md:w-1/5">
                <h4>{{ $t('documents.docs_and_grades') }}</h4>
              </div>
              <div class="vx-col mb-3 w-full md:w-4/5">
                <vs-row>
                  <div class="vx-col mb-3 w-full md:w-1/3">
                    <vs-row class="font-semibold">{{ $t('documents.grade') }}</vs-row>
                    <vs-row class="mt-3">{{ document.grade }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full md:w-1/3">
                    <vs-row class="font-semibold">{{ $t('documents.field_of_study') }}</vs-row>
                    <vs-row class="mt-3">{{ document.fieldOfStudy }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full md:w-1/3">
                    <vs-row class="font-semibold">{{ $t('documents.license_no') }}</vs-row>
                    <vs-row class="mt-3">{{ document.licenseNo.join(', ') }}</vs-row>
                  </div>
                  <div class="vx-col mb-3 w-full" v-if="document.description">
                    <vs-row class="font-semibold">{{ $t('documents.description') }}</vs-row>
                    <vs-row class="mt-3">{{ document.description }}</vs-row>
                  </div>
                  <download-files class="mb-base" :files="document.attachments" />
                </vs-row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import moduleDocument from '@/store/document/moduleDocument'
import UploadFile from '@/views/components/forms/UploadFile'
import DownloadFiles from '@/views/components/forms/DownloadFiles'

export default {
  components: { DownloadFiles, UploadFile },
  props: {
    data: {
      type: Object,
      required: true
    },
    showHeader: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      document: {},
      createDocument: true,
      startUpload: false,
      canSubmit: true,
      locales: []
    }
  },
  methods: {
    handlePendingUpload(value) {
      this.canSubmit = !value
    },
    saveDocuments() {
      this.$validator.validateAll().then(result => {
        if (!this.canSubmit) this.startUpload = true
        else if (result) {
          this.$vs.loading({
            container: '#tab-loading',
            scale: 0.7
          })
          this.$store
            .dispatch('document/createDocument', {
              ...this.document,
              userId: this.$store.state.AppActiveUser.id,
              type: 'employment'
            })
            .then(response => {
              this.$store
                .dispatch('auth/editProfile', { employmentDoc: response.data.id })
                .then(() => {
                  this.$store.commit('UPDATE_USER_INFO', {
                    employmentDoc: response.data.id
                  })
                  this.document = response.data
                  this.createDocument = false
                  this.$notifySuccess(this.$t('forms.successfully_created'))
                })
                .finally(() => this.$vs.loading.close('#tab-loading > .con-vs-loading'))
            })
            .catch(() => this.$vs.loading.close('#tab-loading > .con-vs-loading'))
        }
      })
    },
    resubmit() {
      this.createDocument = true
      this.document.attachments = []
      delete this.document.state
      delete this.document.expirationDate
      delete this.document.approvedSubjects
      delete this.document.rejectMessage
    },
    saveLocales() {
      this.$store
        .dispatch('auth/editProfile', { languages: this.locales })
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$store.commit('UPDATE_USER_INFO', {
            displayName: `${res.data.details.firstName} ${res.data.details.lastName}`,
            ...res.data
          })
          this.$notifySuccess(this.$t('forms.successfully_edited'))
        })
    }
  },
  created() {
    this.locales = this.data.languages

    if (!moduleDocument.isRegistered) {
      this.$store.registerModule('document', moduleDocument)
      moduleDocument.isRegistered = true
    }
    if (this.data.employmentDoc) {
      this.$store
        .dispatch('document/fetchDocument', this.data.employmentDoc)
        .then(res => {
          this.document = res.data
          this.createDocument = false
        })
        .finally(() => {
          this.$vs.loading.close('#div-loading > .con-vs-loading')
        })
    }
  }
}
</script>
